<template>
  <v-container
    class="dashboard-presentation py-0 my-0"
    :class="{ 'py-md-4': emailFunnelsLoading || analyticsImpressionsLoading }"
    fluid
  >
    <DetailsDialog
      v-if="showDialog"
      @close-details="showDialog = false"
      :component="component"
      :component-value="componentValue"
    />
    <v-row v-show="emailFunnelsLoading || analyticsImpressionsLoading">
      <v-col
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-skeleton-loader
          height="60px"
          width="100%"
          type="text"
        />
        <v-skeleton-loader
          max-height="300px"
          :style="`background: ${getBoxColor('box1').base}; border-radius: 15px`"
          class="elevation-1 rounded-md"
          width="100%"
          type="table-heading, article, actions"
        />
        <v-skeleton-loader
          max-height="200px"
          :style="`background: ${getBoxColor('box2').base}; border-radius: 15px`"
          class="elevation-1 rounded-md mx-auto"
          width="95%"
          type="article"
        />
        <v-skeleton-loader
          max-height="250px"
          :style="`background: ${getBoxColor('box3').base}; border-radius: 15px`"
          class="elevation-1 rounded-md mx-auto"
          width="90%"
          type="table-heading, article, actions"
        />
        <v-skeleton-loader
          max-height="250px"
          :style="`background: ${getBoxColor('box4').base}; border-radius: 15px`"
          class="elevation-1 rounded-md mx-auto"
          width="85%"
          type="article"
        />
        <v-skeleton-loader
          v-if="!isNewUser || isMasterView"
          max-height="250px"
          :style="`background: ${getBoxColor('box5').base}; border-radius: 15px`"
          class="elevation-1 rounded-md mx-auto"
          width="90%"
          type="article"
        />
      </v-col>
      <v-col
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-skeleton-loader
          max-height="300px"
          width="100%"
          type="image"
        />
      </v-col>
    </v-row>
    <v-row
      v-show="!emailFunnelsLoading && !analyticsImpressionsLoading"
      :class="{ 'mt-0': !emailFunnelsLoading && !analyticsImpressionsLoading }"
    >
      <!-- Left Column -->
      <v-col
        v-show="!emailFunnelsLoading && !analyticsImpressionsLoading"
        class="pa-0 ma-0 pr-md-2"
        cols="12"
        md="6"
      >
        <v-card
          border
          class="box px-4 py-0 pt-md-4 mr-md-2"
        >
          <v-container
            class="px-0 py-0 pt-4 pb-md-4"
            fluid
          >
            <v-row>
              <Feature :feature-slug="featureNames.DASHBOARD_TIME_FILTER">
                <v-col
                  class="d-flex justify-end pt-0"
                  v-if="isExpert && !isNewUser"
                >
                  <div class="dashboard-date-picker--width">
                    <DatePicker />
                  </div>
                </v-col>
              </Feature>
              <v-col
                v-if="isNewUser && !isMasterView"
                class="pa-0 ma-0"
                cols="12"
              >
                <GainedContacts
                  :statistics="statistics"
                  :loading="loading"
                  :is-new-user="isNewUser"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <ContactAcquisition
                  @update-stats="handleContactAquisitionStatsUpdate"
                  @open-details="
                    (openDetails('contactAcquisitionDetails', $event), (showDialog = true))
                  "
                  :is-light-user="isLightUser"
                  :is-new-user="isNewUser"
                  :show-view-details-link="showViewDetailsLink"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <Impressions
                  @analytics-impressions-loading-update="handleAnalyticsImpressionsLoading"
                  @open-details="(openDetails('impressionsDetails'), (showDialog = true))"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                  :show-view-details-link="showViewDetailsLink"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <EmailFunnels
                  @email-funnel-loading-update="handleEmailFunnelsLoading"
                  @update-stats="handleEmailFunnelsStatsUpdate"
                  @open-details="(openDetails('emailFunnelsDetails'), (showDialog = true))"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                  :show-view-details-link="showViewDetailsLink"
                />
              </v-col>
              <v-col
                v-if="!isNewUser || isMasterView"
                class="pa-0 ma-0"
                cols="12"
              >
                <GainedContacts
                  :statistics="statistics"
                  :loading="loading"
                  :is-new-user="isNewUser"
                  :is-light-user="isLightUser"
                  @open-details="(openDetails('gainedContactsDetails'), (showDialog = true))"
                />
              </v-col>
              <v-col
                class="pa-0 ma-0"
                cols="12"
              >
                <AddedValue
                  :statistics="statistics"
                  :loading="loading"
                  :total-time-saved="totalTimeSaved"
                  :total-money-saved="totalMoneySaved"
                  v-if="!isNewUser || isMasterView"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>

      <!-- Right Column -->
      <v-col
        class="pa-0 ma-0 mt-4 mt-md-0"
        cols="12"
        md="6"
      >
        <Feature :feature-slug="featureNames.NEXT_EMAILS">
          <v-card
            v-if="!isMasterView"
            border
            class="box px-4 py-0 pt-md-4 mr-0 mb-4"
          >
            <next-emails />
          </v-card>
        </Feature>

        <Feature :feature-slug="featureNames.KONSTANTINE">
          <konstantine class="mb-4" />
        </Feature>
        <template v-if="leads">
          <Permission permission="lead:read">
            <Feature
              v-slot="{ feature }"
              :feature-slug="featureNames.COMPANY"
            >
              <Feature
                :feature-slug="featureNames.VALUATION"
                class="w-10"
              >
                <lead-map :leads="leads" />
              </Feature>
              <lead-interactivity
                class="mt-4"
                v-if="feature.config.showLeadInteractivity"
              />
            </Feature>
          </Permission>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, ref } from 'vue'
import { useRoute } from 'vue-router'

import { AuthInstance } from '@/auth'
import Permission from '@/components/Permission'
import { useBranding } from '@/composables/useBranding'
import { Lead, Statistics } from '@/generated/graphql'
import featureNames from '@/lib/featureNames'
import { DashboardBranding } from '@/lib/features'
import { isExpertUser, ProductType } from '@/lib/productTypes'
import COMPANY from '@/modules/company/queries/Company.gql'
import AddedValue from '@/modules/dashboard/AddedValue.vue'
import ContactAcquisition from '@/modules/dashboard/ContactAcquisition.vue'
import DetailsDialog from '@/modules/dashboard/dashboardDetails/detailsDialog.vue'
import EmailFunnels from '@/modules/dashboard/EmailFunnels.vue'
import GainedContacts from '@/modules/dashboard/GainedContacts.vue'
import GET_ANALYTICS_IMPRESSIONS from '@/modules/dashboard/GetAnalyticsImpressionForCompany.gql'
import Impressions from '@/modules/dashboard/Impressions.vue'
import Konstantine from '@/modules/dashboard/konstantine/Konstantine.vue'
import NextEmails from '@/modules/dashboard/nextEmails/NextEmails.vue'
import LeadMap from '@/modules/map/index.vue'
import EMAIL_FUNNELS_STATISTICS from '@/modules/statistics/GetCompanyEmailFunnelsStatistics.gql'
import LeadInteractivity from '@/modules/statistics/LeadInteractivity.vue'
import { Feature } from '@/plugins/feature-config'
import { TrackingPlugin } from '@/plugins/tracking'

import DatePicker from './DatePicker.vue'

type Props = {
  statistics: Statistics
  leads: Lead[]
  loading: boolean
}

defineProps<Props>()

const $auth = inject<AuthInstance>('auth')
const $tracking = inject<TrackingPlugin>('tracking')

const contactAquisitionTimeSaved = ref(0)
const contactAquisitionMoneySaved = ref(0)
const emailFunnelsTimeSaved = ref(0)
const emailFunnlesMoneySaved = ref(0)
const emailFunnelsLoading = ref(true)
const analyticsImpressionsLoading = ref(true)
const route = useRoute()
const { dashboardBoxesBackground } = useBranding()

const isMasterView = computed(() => route.path.startsWith('/master'))
const routeParamId = computed(() => route?.params?.id || null)
const hasRouteId = computed<boolean>(() => !!routeParamId.value)

const { result: companyResluts } = useQuery(
  COMPANY,
  { id: $auth.user.companyId },
  {
    fetchPolicy: 'cache-first',
  }
)
const allTimeVariables = computed(() => ({
  input: {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value ?? null,
  },
}))

const { result: allTimeEmailFunnelsResult } = useQuery(EMAIL_FUNNELS_STATISTICS, allTimeVariables)

const { result: allTimeAnalyticsResult } = useQuery(GET_ANALYTICS_IMPRESSIONS, allTimeVariables)

const isExpert = computed(() => isExpertUser($auth.user))

const handleEmailFunnelsLoading = (loading: boolean) => {
  emailFunnelsLoading.value = loading
}
const handleAnalyticsImpressionsLoading = (loading: boolean) => {
  analyticsImpressionsLoading.value = loading
}

const isLightUser = computed(
  () => companyResluts?.value?.company?.productType === ProductType.LIGHT
)

const totalTimeSaved = computed(
  () => contactAquisitionTimeSaved.value + emailFunnelsTimeSaved.value
)
const totalMoneySaved = computed(
  () => contactAquisitionMoneySaved.value + emailFunnlesMoneySaved.value
)

interface StatsUpdate {
  timeSaved: number
  moneySaved: number
}

const getBoxColor = (box: string) => dashboardBoxesBackground.value[box] as DashboardBranding

const handleContactAquisitionStatsUpdate = ({ timeSaved, moneySaved }: StatsUpdate) => {
  contactAquisitionTimeSaved.value = timeSaved
  contactAquisitionMoneySaved.value = moneySaved
}

const handleEmailFunnelsStatsUpdate = ({ timeSaved, moneySaved }: StatsUpdate) => {
  emailFunnelsTimeSaved.value = timeSaved
  emailFunnlesMoneySaved.value = moneySaved
}

const allTimeSentEmails = computed(
  () => allTimeEmailFunnelsResult.value?.companyEmailFunnelsStatistics?.sent ?? 0
)

const allTimeImpressions = computed(
  () => allTimeAnalyticsResult.value?.analyticsImpression?.overallTotal ?? 0
)

const isNewUser = computed(() => {
  return !(allTimeSentEmails.value > 0 && allTimeImpressions.value > 0)
})

const openDetails = (newComponent, data = null) => {
  component.value = newComponent
  componentValue.value = data ?? null
  $tracking.event('Dashboard', 'Clicked', 'Open Details', newComponent)
}
const showDialog = ref(false)
const component = ref('contactAcquisitionDetails')
const componentValue = ref(null)
const showViewDetailsLink = computed<boolean>(
  () =>
    !isLightUser.value &&
    ((!hasRouteId.value && !isNewUser.value) || (isMasterView.value && hasRouteId.value))
)
</script>

<style>
.dashboard-presentation {
  .card {
    border-radius: 10px;
    color: #ffffff;
    padding: 10px 15px;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }
  .card-new-user {
    border-radius: 10px;
    color: #ffffff;
    padding: 10px 15px;
    justify-content: center;
    align-items: center;
    gap: 2px;
  }
  .vertical-divider {
    font-size: 22px;
    opacity: 0.3;
    color: #ffffff;
  }
  .save-tme-money-numbers {
    display: flex;
    align-items: center;
  }
  .save-time-money {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .funnel,
  .funnel-new-user {
    margin: 0 auto;
    position: relative;
    margin-bottom: 10px;
  }

  .grid {
    display: grid;
  }

  .icon {
    font-size: 18px;
  }

  .title {
    color: #ffffff;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 26px;
  }

  .label {
    color: #ffffff;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }

  .number {
    color: #ffffff;
    font-size: 22px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
  }

  .button {
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .button-invite-more,
  .button-do-more {
    width: 100%;
  }

  .dashboard-date-picker--width {
    width: 260px;
  }

  @media (max-width: 960px) {
    .funnel,
    .funnel-new-user {
      width: 100%;
    }
    .funnel::after {
      left: 6px !important;
      right: 6px !important;
    }
    .funnel-new-user::after {
      left: 8px !important;
      right: 8px !important;
    }
    .title {
      font-size: 14px;
    }
    .label {
      font-size: 12px;
    }
    .number {
      font-size: 16px;
    }
    .button {
      font-size: 12px;
    }
    .v-btn--size-default {
      width: 100%;
    }

    .icon {
      font-size: 16px;
    }
  }
}
</style>
